/**
 * Quill 编辑器图片实时缩放样式应用工具
 * 
 * 用于在富文本编辑器中实时应用图片缩放和布局样式，实现所见即所得效果
 */

/**
 * 为 Quill 编辑器内的所有图片应用缩放样式
 * @param editorElement - Quill 编辑器的 DOM 元素
 * @param imageWidths - 图片宽度百分比数组（0-100）
 */
export function applyQuillImageStyles(
  editorElement: HTMLElement | null,
  imageWidths: number[] = []
) {
  if (!editorElement || !editorElement.isConnected) return;

  const images = editorElement.querySelectorAll('img');

  images.forEach((img, idx) => {
    const width = imageWidths[idx] ?? 100; // 默认 100%

    // 应用缩放样式
    img.style.width = `${width}%`;
    img.style.height = 'auto';
    img.style.objectFit = 'contain';
    // 不再设置 display，由 CSS 默认值控制
    img.style.margin = '0';

    // 添加过渡动画使缩放更平滑
    img.style.transition = 'width 0.2s ease';
  });
}

/**
 * 监听 Quill 编辑器内容变化并自动应用图片样式
 * @param editorElement - Quill 编辑器的 DOM 元素
 * @param imageWidths - 图片宽度百分比数组
 * @returns 清理函数，用于停止监听
 */
export function watchQuillImageChanges(
  editorElement: HTMLElement | null,
  imageWidths: number[] = []
): () => void {
  if (!editorElement) return () => {};

  // 初始应用
  applyQuillImageStyles(editorElement, imageWidths);

  // 使用 MutationObserver 监听 DOM 变化
  const observer = new MutationObserver(() => {
    // 如果编辑器元素已不在文档树中，立即断开观察器并返回
    if (!editorElement || !editorElement.isConnected) {
      observer.disconnect();
      return;
    }
    try {
      applyQuillImageStyles(editorElement, imageWidths);
    } catch {
      // DOM 节点已被移除（组件卸载期间），静默忽略避免 Uncaught 异常
    }
  });

  observer.observe(editorElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'] // 只监听 src 属性变化
  });

  // 返回清理函数
  return () => observer.disconnect();
}
